import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '/api')
  : '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('user_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

const ApiService = {
  getChats: async () => {
    try {
      const response = await api.get('/chats');
      return response.data;
    } catch (error) {
      throw new Error(`Error loading chats: ${error.message}`);
    }
  },

  getChat: async (chatId) => {
    try {
      const response = await api.get(`/chats/${chatId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error loading chat: ${error.message}`);
    }
  },

  getInviteCandidates: async (chatId) => {
    try {
      const response = await api.get(`/chats/${chatId}/invite-candidates`);
      return response.data || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error loading invite candidates: ${error.message}`);
    }
  },

  inviteToDialog: async (chatId, targetUserId) => {
    try {
      const response = await api.post(`/chats/${chatId}/invite`, { targetUserId });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error inviting user: ${error.message}`);
    }
  },

  kickFromDialog: async (chatId, targetUserId) => {
    try {
      const response = await api.post(`/chats/${chatId}/kick`, { targetUserId });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error kicking user: ${error.message}`);
    }
  },

  leaveDialog: async (chatId) => {
    try {
      const response = await api.post(`/chats/${chatId}/leave`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error leaving dialog: ${error.message}`);
    }
  },

  joinByLink: async (chatId) => {
    try {
      const response = await api.post('/chats/join-by-link', { chatId });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error joining dialog by link: ${error.message}`);
    }
  },

  getPendingJoinRequests: async () => {
    try {
      const response = await api.get('/chats/join-requests/pending');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(`Error loading pending join requests: ${error.message}`);
    }
  },

  acceptJoinRequest: async (requestId) => {
    try {
      const response = await api.post(`/chats/join-requests/${requestId}/accept`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error accepting join request: ${error.message}`);
    }
  },

  rejectJoinRequest: async (requestId) => {
    try {
      const response = await api.post(`/chats/join-requests/${requestId}/reject`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error rejecting join request: ${error.message}`);
    }
  },

  getDialogParticipants: async (chatId) => {
    try {
      const response = await api.get(`/chats/${chatId}/participants`);
      return response.data || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error loading dialog participants: ${error.message}`);
    }
  },

  markChatAsRead: async (chatId) => {
    try {
      const response = await api.post(`/chats/${chatId}/read`);
      return response.data;
    } catch (error) {
      throw new Error(`Error marking chat as read: ${error.message}`);
    }
  },

  markChatAsUnread: async (chatId) => {
    try {
      const response = await api.post(`/chats/${chatId}/unread`);
      return response.data;
    } catch (error) {
      throw new Error(`Error marking chat as unread: ${error.message}`);
    }
  },

  deleteDialog: async (chatId, password) => {
    try {
      const response = await api.post(`/chats/${chatId}/delete-dialog`, { password });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error deleting dialog: ${error.message}`);
    }
  },

  getMessages: async (chatId, limit = 50, offset = 0) => {
    try {
      const response = await api.get(`/messages?chatId=${chatId}&limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error loading messages: ${error.message}`);
    }
  },

  sendMessage: async (message) => {
    try {
      const payload = {
        chatId: message.chatId,
        text: message.text,
        timestamp: new Date().toISOString(),
        isFromBot: message.isFromBot || false
      };

      if (message.attachments) {
        // Send attachments as JSON string to avoid ambiguous object binding on backend.
        payload.attachments = JSON.stringify(message.attachments);
      }
      if (message.format) {
        payload.format = message.format;
      }

      const response = await api.post('/messages', payload);
      return response.data;
    } catch (error) {
      throw new Error(`Error sending message: ${error.message}`);
    }
  },

  uploadAttachment: async (file, type = 'file') => {
    try {
      const formData = new FormData();
      const incomingMime = String(file?.type || '').trim().toLowerCase();
      const fileName = String(file?.name || '').toLowerCase();
      const inferredMime = (() => {
        if (fileName.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        if (fileName.endsWith('.doc')) return 'application/msword';
        if (fileName.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        if (fileName.endsWith('.xls')) return 'application/vnd.ms-excel';
        if (fileName.endsWith('.pptx')) return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        if (fileName.endsWith('.ppt')) return 'application/vnd.ms-powerpoint';
        if (fileName.endsWith('.pdf')) return 'application/pdf';
        if (fileName.endsWith('.txt')) return 'text/plain';
        if (fileName.endsWith('.zip')) return 'application/zip';
        if (fileName.endsWith('.mp4')) return 'video/mp4';
        if (fileName.endsWith('.webm')) return 'video/webm';
        if (fileName.endsWith('.mp3')) return 'audio/mpeg';
        if (fileName.endsWith('.wav')) return 'audio/wav';
        if (fileName.endsWith('.ogg')) return 'audio/ogg';
        if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) return 'image/jpeg';
        if (fileName.endsWith('.png')) return 'image/png';
        if (fileName.endsWith('.gif')) return 'image/gif';
        if (fileName.endsWith('.webp')) return 'image/webp';
        return '';
      })();
      const safeMime = incomingMime || inferredMime || 'application/octet-stream';
      const uploadBlob = safeMime === incomingMime ? file : new Blob([file], { type: safeMime });
      formData.append('file', uploadBlob, file?.name || 'file');
      formData.append('type', type);

      const response = await api.post('/messages/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data?.attachment;
    } catch (error) {
      if (error.response?.status === 413) {
        throw new Error('Файл слишком большой (HTTP 413). Уменьшите размер файла.');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Error uploading attachment: ${error.message}`);
    }
  },

  editMessage: async (messageId, text) => {
    try {
      const response = await api.put(`/messages/${messageId}`, { text });
      return response.data;
    } catch (error) {
      throw new Error(`Error editing message: ${error.message}`);
    }
  },

  deleteMessage: async (messageId) => {
    try {
      await api.delete('/messages', { params: { message_id: messageId } });
    } catch (error) {
      throw new Error(`Error deleting message: ${error.message}`);
    }
  },

  requestContact: async (chatId, text) => {
    try {
      const response = await api.post('/messages/request-contact', { chatId, text });
      return response.data;
    } catch (error) {
      throw new Error(`Error requesting contact: ${error.message}`);
    }
  },

  sendSmsForward: async (phone, text) => {
    try {
      const response = await api.post('/tools/sms-forward', { phone, text });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          const fallbackResponse = await api.post('/messages/sms-forward', { phone, text });
          return fallbackResponse.data;
        } catch (fallbackError) {
          if (fallbackError.response?.data?.message) {
            throw new Error(fallbackError.response.data.message);
          }
          throw new Error(`SMS send error: ${fallbackError.message}`);
        }
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`SMS send error: ${error.message}`);
    }
  },

  getOnlineOperators: async () => {
    try {
      const response = await api.get('/tools/online-operators');
      return response.data || [];
    } catch (error) {
      throw new Error(`Online operators error: ${error.message}`);
    }
  },

  getTelegramUsers: async () => {
    try {
      const response = await api.get('/tools/telegram-users');
      return response.data || [];
    } catch (error) {
      throw new Error(`Telegram users error: ${error.message}`);
    }
  },

  sendTelegramDirect: async (userId, text) => {
    try {
      const response = await api.post('/tools/telegram-send', { userId, text });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Telegram send error: ${error.message}`);
    }
  },

  sendMassChatMessage: async (password, text) => {
    try {
      const response = await api.post('/tools/mass-chat-send', { password, text });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Mass send error: ${error.message}`);
    }
  },

  searchMessages: async (chatId, query) => {
    try {
      const response = await api.get(`/messages/search?chatId=${chatId}&q=${query}`);
      return response.data;
    } catch (error) {
      throw new Error(`Search error: ${error.message}`);
    }
  },

  login: async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(`Login error: ${error.message}`);
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('user_token');
      localStorage.removeItem('user');
    } catch {
      localStorage.removeItem('user_token');
      localStorage.removeItem('user');
    }
  },

  checkAuth: async () => {
    try {
      const response = await api.get('/auth/check');
      return response.data;
    } catch {
      throw new Error('Not authenticated');
    }
  }
};

ApiService.api = api;

export default ApiService;
